import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { findDOMNode } from 'react-dom';
import invariant from 'react-utils/invariant';
import PropTypes from 'prop-types';
import { callIfPossible } from '../core/Functions';
import ChildrenWrapper from '../utils/ChildrenWrapper';
import domElementPropType from '../utils/propTypes/domElement';

const UIIntersectionObserver = props => {
  const {
    renderContent,
    root,
    rootMargin,
    threshold,
    target,
    onIntersectionChange,
    initialIntersecting
  } = props;
  const {
    IntersectionObserver
  } = window;
  const observerRef = useRef();
  const [childrenWrapperRef, setChildrenWrapperRef] = useState();
  const [isIntersecting, setIsIntersecting] = useState(initialIntersecting);
  const targetElement = target || findDOMNode(childrenWrapperRef);

  if (process.env.NODE_ENV !== 'production') {
    invariant(!threshold || threshold >= 0 && threshold <= 1, 'UIIntersectionObserver: `threshold` should be a value between 0 and 1 (received %s)', threshold);
  }

  const stopObserver = useCallback(() => {
    if (observerRef && observerRef.current) {
      observerRef.current.disconnect();
    }
  }, [observerRef]);
  const handleIntersection = useCallback(entries => {
    const {
      isIntersecting: intersecting
    } = entries[0];
    setIsIntersecting(intersecting);
    callIfPossible(onIntersectionChange, {
      entries,
      observer: observerRef.current
    });
  }, [onIntersectionChange]);
  useEffect(() => {
    const startObserver = () => {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root,
        rootMargin: rootMargin,
        threshold
      });
      observerRef.current.observe(targetElement);
    };

    if (IntersectionObserver && targetElement) {
      startObserver();
    }

    return stopObserver;
  }, [IntersectionObserver, root, rootMargin, stopObserver, threshold, targetElement, handleIntersection]);

  if (!renderContent) {
    return null;
  }

  return /*#__PURE__*/_jsx(ChildrenWrapper, {
    ref: setChildrenWrapperRef,
    children: renderContent(isIntersecting)
  });
};

UIIntersectionObserver.propTypes = {
  renderContent: PropTypes.func,
  target: domElementPropType,
  root: domElementPropType,

  /**
   * Must be specified in pixels or percent.
   * When `rootMargin` is not specified, it will default to string `"0px 0px 0px 0px"`.
   */
  rootMargin: PropTypes.string,
  threshold: PropTypes.number,
  onIntersectionChange: PropTypes.func,
  initialIntersecting: PropTypes.bool
};
UIIntersectionObserver.defaultProps = {
  initialIntersecting: false
};
UIIntersectionObserver.displayName = 'UIIntersectionObserver';
export default UIIntersectionObserver;